/**
 * ONE LOAD SCHEDULER FOR THE WHOLE APP (docs/asset-loading.md law 3).
 *
 * The owner, 26 September 2026: the flaw was "loading all arts instead of just
 * the ones needed"; "the rest should still be loaded asap but not cause extra
 * load". So only what the current or next screen shows loads first, and
 * everything else loads as soon as possible right after, in the background,
 * never competing with foreground loads. About a dozen "warm ahead" paths
 * each made that call on their own and together started 8 MB of pictures
 * within ~2 s of the lobby appearing (scratchpad designs/loadScheduler.md §2).
 * This module makes the call once, for everyone.
 *
 * ── FOUR CLASSES ────────────────────────────────────────────────────────────
 *
 *   foreground-gated  A reveal waits for it (the boot veil, the match bar, a
 *                     surface hold, the mulligan). Starts at once, high
 *                     priority, decoded and handed to the raster sampler.
 *   foreground        On screen but not waited for (a drawn card, an
 *                     opponent's play). Starts at once at the browser's own
 *                     priority (high in WebKit, see PRIORITY ONCE SENT
 *                     below), decoded and kept.
 *   background-soon   Likely next: the next screen's first view along the
 *                     flow the player is on. Starts only when no foreground
 *                     request is in flight, 150 ms after the last foreground
 *                     DATA read (its pictures usually follow it), 250 ms after
 *                     a press, after a short idle slot (≤ 300 ms). Two at a
 *                     time; one for same-origin files when the page came over
 *                     HTTP/1.1 (it holds one of six sockets); one when the link
 *                     is constrained. Bytes only. Low priority (in WebKit the
 *                     browser's own, and the flow's next view the priority
 *                     its page turn asks for: PRIORITY ONCE SENT).
 *   background-idle   Possible later. Only when no soon work is waiting or
 *                     running, in a true idle slot, 400 ms after a press, on a
 *                     visible page, one at a time, never under Save-Data or on
 *                     a slow link (constrained: Save-Data, prefers-reduced-data,
 *                     or an effectiveType of slow-2g, 2g or 3g).
 *
 * "Paused" cannot stop bytes already on the wire, so it means no NEW
 * background start. What is in flight is pre-empted when a foreground PICTURE
 * starts: idle work always, soon work while less than 75% has arrived (a
 * download that has not answered yet costs nothing to stop). Aborted work
 * goes back to the head of its queue. A data read pauses background but never
 * pre-empts it: aborting a 400 KB tail to speed up a 2 KB answer wastes more
 * than it saves. Everything that counts as foreground stops counting after
 * 20 s (a hung request must not starve the background for good).
 *
 * ── HOW A SCREEN USES IT ────────────────────────────────────────────────────
 *
 *   const next = LoadScheduler.scope('collection', { runsOn: ['collection'] });
 *   await AssetPreloader.gateWarm(next.gated(pageUrls));   // the reveal's set
 *   void next.read('background-soon', (signal) => readPage(n + 1, { signal }))
 *     .then((page) => page && next.idle(artOf(page), { decode: true }));
 *   tab.addEventListener('pointerenter', () => next.promote(tabArt, 'background-soon'));
 *   // a new query or the surface's close:
 *   next.close();
 *
 *   gated(pictures)        → Promise<{ ready, failed, cancelled }>, never rejects;
 *                            the CALLER caps its wait (AssetPreloader.gateWarm).
 *   show(pictures)         foreground, not waited for.
 *   soon / idle(pictures, { decode })
 *                          bytes only (fetched into the HTTP cache in the
 *                          picture's one request mode) unless decode is asked
 *                          for: only a page built ahead needs pixels, and a
 *                          decoded warm can evict the pixels on screen from the
 *                          sampler's bounded store. Resolves when all settle.
 *                          A picture given as { url, rank } waits in rank order
 *                          (lower first, 0 by default): the flow's next view
 *                          (the Collection's next page) passes a rank below 0
 *                          and goes ahead of other surfaces' work already
 *                          waiting, re-ranking a URL they queued first. A rank
 *                          below 0 promises it is asked for only once the
 *                          screen's own pictures are in: in WebKit it is sent
 *                          at the priority its page turn will ask for
 *                          (PRIORITY ONCE SENT).
 *   read(cls, run)        a background data read: `run(signal)` must pass the
 *                          signal to its fetch (or Convex call), which marks it
 *                          background and cancels it when the scope closes.
 *                          Resolves the answer, or undefined when cancelled or
 *                          failed (log inside `run` if the failure matters).
 *   promote(pictures, cls) intent (hover, focus, first touch, press) moves
 *                          queued or in-flight work UP a class, never down; an
 *                          in-flight download is re-classed and asked for again
 *                          at its new priority, never restarted (PRIORITY ONCE
 *                          SENT below).
 *   close()                drops queued work and aborts in-flight BACKGROUND
 *                          work no other scope still holds. Foreground loads
 *                          are never cancelled by a scope.
 *   settled()              resolves once the scope has nothing queued/running.
 *   waitWhile(reason, isBusy, capMs)
 *                          this scope's background waits while `isBusy()`
 *                          (polled every 250 ms), for at most capMs at a time
 *                          (Infinity: for as long as it is busy, for a cover
 *                          the player chose, such as a lobby surface over the
 *                          lobby a warm is for, rather than one that can hang).
 *
 *   Scope options: `runsOn` (the screens its background may run on; '*' for
 *   all), `maxInFlight` (a cap below the class limit), `admit(policy)`
 *   (checked before every start; false drops the scope's queued work, so a
 *   link that degrades stops it at the next picture), `steady` (its soon
 *   pictures run at its own maxInFlight, yielding their starts only to a
 *   picture the screen is loading — no idle slot, no quiet after presses or
 *   data reads, no global two-download limit, never pre-empted; the match's
 *   art plan, asset-preloader.ts, owner 28 September 2026).
 *
 *   NOT WIRED (28 September 2026): the app calls neither enter() nor
 *   install(). `screen` stays null, so runsOn never pauses a scope, and a
 *   scope ends with its close() (the match's plan: AssetPreloader
 *   .endMatchWarm when the player returns to the lobby). install()'s fetch
 *   observer would add a quiet after every data read to background work; it
 *   stays off while the owner's rule favours loading over pausing.
 *
 *   LoadScheduler.enter(screen)   a navigation. Scopes whose runsOn excludes
 *                                 it pause (queued work waits, idle downloads
 *                                 stop); entering 'match-loading' CLOSES every
 *                                 scope that does not run there, so nothing
 *                                 from the lobby competes with the first hand.
 *   LoadScheduler.hold(reason, capMs) → release()
 *                                 no background starts while held (the boot
 *                                 veil; the match's first screen).
 *   LoadScheduler.holdWhile(reason, isBusy, capMs) → remove()
 *                                 the polled form, for covers known only by the
 *                                 DOM.
 *   LoadScheduler.observeImage(img, cls, url?)
 *                                 count an element's own load as foreground,
 *                                 from just before its src is set to its load
 *                                 or error (or its src being removed).
 *   LoadScheduler.image(url, cls, {signal}?) the shared decoded Image per URL
 *                                 (resolves null on failure, or when it is
 *                                 already decoded and no sampler holds it).
 *   LoadScheduler.images(urls, cls, {signal}?)
 *                                 several asked for at once (a page, a hand),
 *                                 one promise each: the ones already on the
 *                                 wire are raised before the fresh ones are
 *                                 sent (PRIORITY ONCE SENT). gated() and
 *                                 show() ask the same way.
 *                                 An explicit abort releases only that caller;
 *                                 another waiter, scope or connected image
 *                                 keeps the download. No timer cancels it.
 *   LoadScheduler.requestMode(url), policy(), ledger(), state().
 *   LoadScheduler.install(window) ONCE, early at boot (main.js): observes every
 *                                 later fetch() — Convex reads included, since
 *                                 its HTTP client calls window.fetch — as
 *                                 foreground until its own body is in
 *                                 (analytics through the /ingest proxy,
 *                                 keepalive beacons and a scope's own
 *                                 background reads never count), and
 *                                 hands the raster sampler its image loader
 *                                 when the sampler offers setImageLoader. Every
 *                                 other entry point works without it; only the
 *                                 fetch observer and the CDN-as-bytes transport
 *                                 need it.
 *
 * ── ONE REQUEST MODE PER PICTURE, ONE TRANSPORT PER ENGINE ──────────────────
 *
 * The art host sends `Vary: Origin` only to a request that carries an Origin,
 * and this server varies every static file by it, so the same URL asked for
 * with CORS and then without downloads twice. A cross-origin picture is always
 * requested with CORS; a same-origin file never is (requestMode).
 *
 * Measured 26 September 2026 (a local probe:
 * loopback server with the art host's exact headers, branded Chrome and
 * Playwright Firefox and WebKit): a CORS fetch() followed by a CORS <img> of
 * the same URL downloads ONCE in Chrome but TWICE in Firefox and WebKit (even
 * without Vary), and a same-origin no-CORS fetch() then <img> twice in WebKit;
 * an off-DOM Image in the same mode is reused by a later <img> in all three.
 * So background bytes go through fetch() — where progress can be read, which
 * the 75% rule needs — only where the engine shares that cache entry (a
 * Chromium engine, known by its User-Agent Client Hints API) AND the page's
 * connect-src is known to allow the host (this origin, or an origin the page
 * has already fetched with CORS, e.g. the art host's sound cues). Everywhere
 * else a low-priority off-DOM Image in the one mode carries the bytes; its
 * progress cannot be read, so the 75% rule falls back to "idle work is always
 * aborted, soon work finishes". The same code runs for players and under
 * automation.
 *
 * ── PRIORITY ONCE SENT ──────────────────────────────────────────────────────
 *
 * A promoted picture must arrive no later than a fresh one would. Setting
 * fetchPriority on an element whose request is already out changes nothing
 * in any engine: on the Collection's Next (round 14 review, 27 September
 * 2026, phone seats) page 2's two pictures already downloading at low
 * priority came in LAST, seconds after the page's fresh pictures, and the
 * turn waited for them. Measured the same day on the meter's shaping proxy
 * (scratchpad schedulerStarve/bench.js: one low picture in flight or queued,
 * then seven fresh high ones, the Collection's page 2 pictures):
 *   - Chromium (HTTP/2): the low stream stays at urgency 3 behind the fresh
 *     urgency-1 streams. A SECOND request for the URL in the same mode at
 *     high priority joins the one download and raises it: it then arrives
 *     among the first. Stopping the first element and asking again a task
 *     later cancels it and downloads it again, last. A low fetch() download
 *     is joined the same way through the HTTP cache.
 *   - WebKit: a request keeps the priority it was sent with. The second
 *     request joins it without raising it, and removing the first element's
 *     src neither raises nor stops it, so every later high request overtakes
 *     it in the queue for a connection.
 *   - Firefox: the second request joins the one download; the low picture
 *     was not overtaken. Removing the first element's src first cancels it
 *     and downloads it again.
 * So a promotion asks again at the new priority and keeps the first element
 * until the picture settles (askAgain), except in WebKit, where nothing sent
 * can be raised. There (Playwright's WebKit through the shaping proxy, the
 * review of 27 September 2026, scratchpad schedStarveFix/prio.js) the art
 * host is reached over HTTP/1.1 on six connections, and a request waiting
 * for one is served by priority, then in the order it was sent. The page's
 * own <img> pictures go at the browser's own priority; fetchPriority 'high'
 * is served ahead of them, 'low' behind them. Soon work sent 'high' so that
 * a promotion would find it high already went ahead of the page's own
 * pictures: they came in 1.0-2.2 s later on a page turn bench, 12-24% later
 * in the reviewer's. So in WebKit:
 *   - background-soon goes at the browser's own priority, never above the
 *     page's own pictures (it waits behind the ones asked for before it);
 *   - an ask that finds one of its pictures on the wire below its own
 *     priority sends its fresh pictures level with it, at the browser's own
 *     priority (never lower: a gated picture sent 'low' would wait behind
 *     everything the page loads). One priority is served in the order sent,
 *     so the picture on the wire arrives first. Measured the same day (five
 *     page turns, six busy connections): the promoted pictures were never
 *     the page's last (with soon sent 'low', in 3 of 5), the page as a whole
 *     was no later than with soon sent 'low', and the page's own other
 *     pictures were 1.7 s sooner on average than with soon sent 'high'. An
 *     ask raises its pictures on the wire first, so the order it lists them
 *     in does not matter (acquireAll);
 *   - the flow's next view (a rank below 0) goes at the priority its page
 *     turn will ask for (backgroundPriority). Its caller asks for it only
 *     once the screen's own pictures are in. In the app (the Collection's
 *     Next after 6 s of lobby, WebKit phone seat over 4G, scratchpad
 *     schedStarveFix/ana.js), at the browser's own priority it waited
 *     behind the Collection's films (WebKit sends media above a picture's
 *     default, and the films keep asking for byte ranges), was still on the
 *     wire at the press, and the ask that levelled the page with it left
 *     the whole page behind the films: 4.6 s a turn over 8 turns, as long as
 *     with soon sent 'low' (4.6 s over 8, the promoted pictures last every
 *     time). Sent at the turn's priority, the turn took 3.4-4.3 s, 3.7 s on
 *     average over 6, like soon sent 'high' (3.7 s over 8), the promoted
 *     pictures were never last, and the screen's own films, sounds and card
 *     backs finished no later;
 *   - a picture on screen goes at the gated priority: a reveal may wait for
 *     it next, and nothing raises it then;
 *   - idle work goes low: it is only possible later. An idle picture a
 *     reveal then waits for stays below the ask's fresh pictures (no screen
 *     declares idle work today).
 * Measured on Playwright's WebKit only; Safari on a phone reaches the art
 * host over HTTP/2 or HTTP/3, where the connection queue above does not
 * exist.
 *
 * No request is ever cancelled by a timer. A cap belongs to whoever waits
 * (gateWarm, a hold), never to the download: a cold phone's first hand can
 * take 18 s at 1.6 Mbps. Only a decode that never settles after its bytes are
 * in is capped (10 s), since those bytes are already cached.
 */

export type LoadClass = 'foreground-gated' | 'foreground' | 'background-soon' | 'background-idle';
export type BackgroundClass = 'background-soon' | 'background-idle';
export type ScreenName =
    | 'boot' | 'lobby' | 'deck-selector' | 'match-loading' | 'match'
    | 'collection' | 'store' | 'foundry';
export type PictureLike = string | { url: string; rank?: number } | null | undefined;
export interface PictureRequestOptions { signal?: AbortSignal }

export interface GateResult { ready: number; failed: number; cancelled: number }

export interface LoadPolicy {
    saveData: boolean;
    effectiveType: string | null;
    constrained: boolean;
    idleAllowed: boolean;
    soonLimit: number;
    idleLimit: number;
    appOriginHttp1: boolean;
    sameOriginSoonLimit: number;
    fetchTransport: boolean;
    /* 'auto': the browser's own (WebKit, PRIORITY ONCE SENT). */
    soonPriority: 'low' | 'auto';
}

export interface LedgerEntry {
    kind: 'picture' | 'read' | 'fetch' | 'element' | 'mode-mismatch' | 'hold';
    path: string;
    host: string;
    cls: LoadClass | null;
    scope: string | null;
    mode: 'cors' | 'no-cors' | null;
    transport: 'image' | 'fetch' | 'read' | null;
    start: number;
    end: number;
    bytes: number | null;
    outcome: 'ok' | 'error' | 'aborted' | 'cancelled' | 'stalled' | 'capped' | 'mismatch';
    lostBytes: number;
}

export interface RequestMode {
    crossOrigin: 'anonymous' | null;
    mode: 'cors' | 'no-cors';
    init: RequestInit;
}

export interface ScopeOptions {
    runsOn?: ReadonlyArray<ScreenName> | '*';
    maxInFlight?: number;
    admit?: (policy: LoadPolicy) => boolean;
    /* The match's own art plan (asset-preloader.ts): its soon pictures run at
       the scope's own maxInFlight, yielding their starts only to a picture
       the screen is loading. See `startSteady`. */
    steady?: boolean;
}

export interface LoadScope {
    readonly name: string;
    readonly closed: boolean;
    gated(pictures: ReadonlyArray<PictureLike>): Promise<GateResult>;
    show(pictures: ReadonlyArray<PictureLike>): void;
    soon(pictures: ReadonlyArray<PictureLike>, options?: { decode?: boolean }): Promise<GateResult>;
    idle(pictures: ReadonlyArray<PictureLike>, options?: { decode?: boolean }): Promise<GateResult>;
    read<T>(cls: BackgroundClass, run: (signal: AbortSignal) => Promise<T> | T): Promise<T | undefined>;
    promote(pictures: ReadonlyArray<PictureLike>, cls: LoadClass): void;
    waitWhile(reason: string, isBusy: () => boolean, capMs?: number): () => void;
    close(): void;
    settled(): Promise<void>;
}

const LoadScheduler = (function () {
    const CLASS_RANK: Record<LoadClass, number> = {
        'background-idle': 0,
        'background-soon': 1,
        foreground: 2,
        'foreground-gated': 3,
    };
    const SOON_LIMIT = 2;
    const CONSTRAINED_SOON_LIMIT = 1;
    const HTTP1_SAME_ORIGIN_LIMIT = 1;
    const IDLE_LIMIT = 1;
    /* A read's pictures usually start a few ms after its body lands (the
       screen is built from the answer); a background start in that gap would
       be pre-empted at once. A picture's end is not followed that way: a
       render starts its pictures together. */
    const FETCH_QUIET_MS = 150;
    /* Nothing slips into the gap between a press and its first foreground
       request. Shared with the Collection's build-ahead (PREBUILD_INPUT_QUIET_MS). */
    const SOON_INPUT_QUIET_MS = 250;
    const IDLE_INPUT_QUIET_MS = 400;
    /* Soon work also waits for the main thread to breathe, but never long:
       a busy page must not starve it (25 September 2026: a 2 s idle wait left
       the deck behind a quick Keep). */
    const SOON_IDLE_SLOT_MS = 300;
    /* Safari has no requestIdleCallback. */
    const IDLE_FALLBACK_MS = 400;
    const FOREGROUND_STALL_MS = 20000;
    const PROBE_POLL_MS = 250;
    const DEFAULT_WAIT_CAP_MS = 10000;
    const DEFAULT_HOLD_CAP_MS = 15000;
    const DECODE_CAP_MS = 10000;
    const PREEMPT_BELOW = 0.75;
    const SETTLED_URL_CAP = 256;
    const LEDGER_CAP = 256;
    const LEARNED_ORIGIN_CAP = 16;
    const CONSTRAINED_EFFECTIVE_TYPES = new Set(['slow-2g', '2g', '3g']);
    /* Product analytics is not something the player waits on. The app sends
       it through its own origin (posthog-init.js api_host = origin + /ingest,
       the proxy server.js mounts there), so the host alone never matches. */
    const ANALYTICS_HOST = /(^|\.)posthog\.com$/i;
    const ANALYTICS_PROXY_PATH = /^\/ingest(\/|$)/;
    const INPUT_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
    /* Shared by every copy of this module (a test reloads it): a second
       install unwraps the first instead of stacking on it. */
    const ORIGINAL_FETCH = Symbol.for('moonfall.loadScheduler.originalFetch');

    type Outcome = 'ready' | 'failed' | 'cancelled';
    type Settled = { outcome: Outcome; image: HTMLImageElement | null; value?: unknown };
    /* null: the browser's own priority, between the two hints. */
    type RequestPriority = 'high' | 'low' | null;
    const PRIORITY_RANK = { low: 0, auto: 1, high: 2 } as const;

    interface Foreground {
        kind: 'picture' | 'fetch' | 'element';
        path: string;
        host: string;
        start: number;
        stalled: boolean;
        onStall: (() => void) | null;
    }

    interface Probe {
        isBusy: () => boolean;
        capMs: number;
        busySince: number | null;
        capped: boolean;
    }

    interface Scope {
        name: string;
        runsOn: ReadonlyArray<ScreenName> | '*';
        maxInFlight: number;
        admit: ((policy: LoadPolicy) => boolean) | null;
        steady: boolean;
        closed: boolean;
        entries: Set<Entry>;
        probes: Map<string, Probe>;
        drained: Array<() => void>;
        api: LoadScope | null;
    }

    interface Entry {
        key: string;
        url: string | null;
        cls: LoadClass;
        state: 'queued' | 'running';
        holders: Set<Scope>;
        primary: Scope | null;
        scopeName: string | null;
        rank: number;
        decode: boolean;
        pinned: boolean;
        abandoned: boolean;
        transport: 'image' | 'fetch' | 'read' | null;
        image: HTMLImageElement | null;
        /* The priority the running request was sent with, and whether its
           bytes are already in (only the decode is left). */
        sentPriority: RequestPriority;
        bytesIn: boolean;
        /* Earlier elements of a picture asked for again (askAgain): they keep
           their request until the entry settles. */
        joined: HTMLImageElement[];
        controller: AbortController | null;
        attempt: number;
        received: number;
        total: number;
        answered: boolean;
        startedAt: number;
        foreground: Foreground | null;
        decodeTimer: ReturnType<typeof setTimeout> | null;
        run: ((signal: AbortSignal) => unknown) | null;
        waiters: Array<{ scope: Scope | null; resolve: (settled: Settled) => void }>;
    }

    interface Element {
        item: Foreground;
        sawSrc: boolean;
        url: string | null;
        end: () => void;
    }

    /* A network read the page made. `item` is null for one that does not
       count (a scope's background signal, a keepalive beacon, analytics): it
       is tracked only so that its body cannot end another read of its URL. */
    interface PendingFetch {
        item: Foreground | null;
        start: number;
        done: boolean;
    }

    let screen: ScreenName | null = null;
    const scopes = new Set<Scope>();
    const active = new Map<string, Entry>();
    const queues: Record<BackgroundClass, Entry[]> = { 'background-soon': [], 'background-idle': [] };
    const settledUrls = new Map<string, 'bytes' | 'decoded'>();
    const foreground = new Set<Foreground>();
    const abandonedPictures = new Set<Entry>();
    let abandonedPictureObserver: MutationObserver | null = null;
    const elements = new Map<HTMLImageElement, Element>();
    const holds = new Set<{ reason: string; until: number }>();
    const globalProbes = new Map<string, Probe>();
    const ledgerEntries: LedgerEntry[] = [];
    const backgroundSignals = new WeakSet<AbortSignal>();
    const corsFetchOrigins = new Set<string>();
    const mismatchWarned = new Set<string>();
    const pendingFetches = new Map<string, PendingFetch[]>();
    const slots: Record<BackgroundClass, 'none' | 'pending' | 'granted'> = {
        'background-soon': 'none',
        'background-idle': 'none',
    };
    let lastInputAt = -Infinity;
    let lastFetchEndAt = -Infinity;
    let wakeTimer: ReturnType<typeof setTimeout> | null = null;
    let wakeAt = Infinity;
    let pumping = false;
    let repump = false;
    let listening = false;
    let installed = false;
    let resourceTimingObserved = false;
    let readSeq = 0;
    let navigationProtocol: string | null | undefined;
    let internalScope: Scope | null = null;
    /* The list ask being made (inAsk), and whether one of its pictures on the
       wire stayed below the ask's priority because the engine cannot raise
       a sent request (WebKit): its fresh pictures then go level with it. */
    let asking: { levelled: boolean } | null = null;

    function now() {
        return Date.now();
    }

    function perfNow() {
        try {
            return performance.now();
        } catch (_) {
            return 0;
        }
    }

    function isForegroundClass(cls: LoadClass) {
        return CLASS_RANK[cls] >= CLASS_RANK.foreground;
    }

    function isBackgroundClass(cls: LoadClass): cls is BackgroundClass {
        return cls === 'background-soon' || cls === 'background-idle';
    }

    function toClass(cls: unknown, fallback: LoadClass): LoadClass {
        return typeof cls === 'string' && Object.prototype.hasOwnProperty.call(CLASS_RANK, cls)
            ? cls as LoadClass
            : fallback;
    }

    /* ── URLs and modes ─────────────────────────────────────────────────── */

    function pageBase() {
        try {
            return document.baseURI || location.href;
        } catch (_) {
            return 'http://localhost/';
        }
    }

    function parseUrl(url: string) {
        try {
            return new URL(url, pageBase());
        } catch (_) {
            return null;
        }
    }

    function pageOrigin() {
        try {
            return location.origin;
        } catch (_) {
            return null;
        }
    }

    function isNetworkUrl(url: string) {
        const parsed = parseUrl(url);
        return Boolean(parsed) && (parsed.protocol === 'http:' || parsed.protocol === 'https:');
    }

    function isCrossOrigin(url: string) {
        const parsed = parseUrl(url);
        if (!parsed || (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')) return false;
        return parsed.origin !== pageOrigin();
    }

    function requestMode(url: string): RequestMode {
        return isCrossOrigin(url)
            ? { crossOrigin: 'anonymous', mode: 'cors', init: { mode: 'cors', credentials: 'same-origin' } }
            : { crossOrigin: null, mode: 'no-cors', init: { mode: 'no-cors', credentials: 'include' } };
    }

    /* Paths only: a query string can carry a token. */
    function describe(url: string | null) {
        const parsed = url ? parseUrl(url) : null;
        if (!parsed) return { path: '', host: '' };
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return { path: parsed.protocol, host: '' };
        return { path: parsed.pathname, host: parsed.origin === pageOrigin() ? '' : parsed.host };
    }

    function pictureUrl(picture: PictureLike): string | null {
        if (typeof picture === 'string') return picture || null;
        if (picture && typeof picture === 'object' && typeof picture.url === 'string') return picture.url || null;
        return null;
    }

    function pictureRank(picture: PictureLike) {
        return picture && typeof picture === 'object' && Number.isFinite(picture.rank) ? Number(picture.rank) : 0;
    }

    /* ── Policy ─────────────────────────────────────────────────────────── */

    function readNavigationProtocol() {
        if (navigationProtocol !== undefined) return navigationProtocol;
        navigationProtocol = null;
        try {
            const entry = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
            navigationProtocol = entry?.nextHopProtocol || null;
        } catch (_) {
            navigationProtocol = null;
        }
        return navigationProtocol;
    }

    /* A Chromium engine: a fetch() and a later <img> of the same URL in the
       same mode share one HTTP-cache entry there, and only there (see the
       header). Its User-Agent Client Hints API is Chromium's own. */
    function sharesFetchCacheWithImages() {
        try {
            return typeof window !== 'undefined'
                && typeof window.fetch === 'function'
                && typeof AbortController === 'function'
                && Boolean((navigator as any).userAgentData);
        } catch (_) {
            return false;
        }
    }

    /* WebKit: a request keeps the priority it was sent with. Nothing raises
       it later (a second request for the URL joins it as it is), and removing
       the element's src does not stop it (see the header). Safari, every iOS
       browser and Playwright's WebKit carry the engine token frozen at
       AppleWebKit/605; Blink's is frozen at 537.36, and Blink has the
       User-Agent Client Hints API. */
    function requestPriorityIsFinal() {
        try {
            return !(navigator as any).userAgentData
                && /AppleWebKit\/6\d\d/.test(String(navigator.userAgent || ''));
        } catch (_) {
            return false;
        }
    }

    /* The priority a load of this class is sent with. Where a request can
       still be raised once sent (askAgain), background goes low and a
       picture on screen at the browser's own priority. Where it cannot
       (WebKit, see the header): background-soon at the browser's own
       priority, the one the page's own pictures go at, never above them,
       and level with the fresh pictures of an ask that then waits for it
       (acquireAll), except the flow's next view (backgroundPriority); idle
       work low, since it is only possible later; a picture on screen at the
       gated priority, since a reveal may wait for it next. */
    function freshPriority(cls: LoadClass): RequestPriority {
        if (cls === 'foreground-gated') return 'high';
        const final = requestPriorityIsFinal();
        if (cls === 'foreground') return final ? 'high' : null;
        if (cls === 'background-soon' && final) return null;
        return 'low';
    }

    function outranks(next: RequestPriority, sent: RequestPriority) {
        return PRIORITY_RANK[next ?? 'auto'] > PRIORITY_RANK[sent ?? 'auto'];
    }

    function computePolicy(): LoadPolicy {
        let saveData = false;
        let effectiveType: string | null = null;
        try {
            /* Network Information API: Chromium and Android only, hence untyped.
               Firefox and Safari report nothing and count as unconstrained. */
            const connection = (navigator as any).connection;
            if (connection) {
                saveData = connection.saveData === true;
                effectiveType = typeof connection.effectiveType === 'string' ? connection.effectiveType : null;
            }
        } catch (_) {
            // An unreadable connection is an unknown one.
        }
        try {
            if (!saveData && typeof window.matchMedia === 'function'
                && window.matchMedia('(prefers-reduced-data: reduce)')?.matches) saveData = true;
        } catch (_) {
            // Unsupported media feature.
        }
        const constrained = saveData || CONSTRAINED_EFFECTIVE_TYPES.has(effectiveType);
        const soonLimit = constrained ? CONSTRAINED_SOON_LIMIT : SOON_LIMIT;
        const appOriginHttp1 = readNavigationProtocol() === 'http/1.1';
        return {
            saveData,
            effectiveType,
            constrained,
            idleAllowed: !constrained,
            soonLimit,
            idleLimit: IDLE_LIMIT,
            appOriginHttp1,
            sameOriginSoonLimit: appOriginHttp1 ? Math.min(soonLimit, HTTP1_SAME_ORIGIN_LIMIT) : soonLimit,
            fetchTransport: sharesFetchCacheWithImages(),
            soonPriority: requestPriorityIsFinal() ? 'auto' : 'low',
        };
    }

    function useFetchTransport(url: string) {
        if (!sharesFetchCacheWithImages()) return false;
        if (!isCrossOrigin(url)) return true;
        const parsed = parseUrl(url);
        return Boolean(parsed) && corsFetchOrigins.has(parsed.origin);
    }

    function pageHidden() {
        try {
            return document.visibilityState === 'hidden';
        } catch (_) {
            return false;
        }
    }

    /* ── Ledger ─────────────────────────────────────────────────────────── */

    function record(entry: Partial<LedgerEntry> & Pick<LedgerEntry, 'kind' | 'outcome'>) {
        ledgerEntries.push({
            path: '',
            host: '',
            cls: null,
            scope: null,
            mode: null,
            transport: null,
            start: 0,
            end: now(),
            bytes: null,
            lostBytes: 0,
            ...entry,
        });
        while (ledgerEntries.length > LEDGER_CAP) ledgerEntries.shift();
    }

    /* ── The raster sampler (a UMD global: it cannot import) ────────────── */

    function sampler() {
        try {
            return (window as any).MoonfallRasterSampler || null;
        } catch (_) {
            return null;
        }
    }

    function retain(url: string, image: HTMLImageElement) {
        try {
            sampler()?.rememberDecodedImage?.(url, image);
        } catch (_) {
            // Best-effort: the picture is still in the browser's cache.
        }
    }

    function samplerImage(url: string): HTMLImageElement | null {
        try {
            return sampler()?.getDecodedImage?.(url) || null;
        } catch (_) {
            return null;
        }
    }

    function rememberSettled(url: string, kind: 'bytes' | 'decoded') {
        const previous = settledUrls.get(url);
        settledUrls.delete(url);
        settledUrls.set(url, previous === 'decoded' ? 'decoded' : kind);
        while (settledUrls.size > SETTLED_URL_CAP) settledUrls.delete(settledUrls.keys().next().value);
    }

    /* Decoded and still owned by the sampler's bounded store. When the store
       has evicted it, only the pixels are gone: it falls back to 'bytes', so
       the next request decodes it again from the cache and, like any cached
       picture, pre-empts nothing. */
    function decodedFresh(url: string) {
        if (settledUrls.get(url) !== 'decoded') return false;
        const hasDecodedImage = sampler()?.hasDecodedImage;
        if (typeof hasDecodedImage !== 'function') return true;
        let fresh = false;
        try {
            fresh = Boolean(hasDecodedImage(url));
        } catch (_) {
            fresh = false;
        }
        if (!fresh) settledUrls.set(url, 'bytes');
        return fresh;
    }

    /* ── Foreground bookkeeping ─────────────────────────────────────────── */

    function addForeground(kind: Foreground['kind'], url: string | null, except: Entry | null) {
        const item: Foreground = { kind, ...describe(url), start: now(), stalled: false, onStall: null };
        foreground.add(item);
        // A data: or blob: picture, or one whose bytes this scheduler already
        // brought into the cache, takes nothing from the link.
        if (kind !== 'fetch' && (!url || (isNetworkUrl(url) && !settledUrls.has(url)))) preemptBackground(except, url);
        return item;
    }

    function endForeground(item: Foreground | null) {
        if (!item || !foreground.delete(item)) return;
        if (item.kind === 'fetch') lastFetchEndAt = now();
        pump();
    }

    function foregroundBlocking(t: number) {
        let count = 0;
        let wake = Infinity;
        for (const item of [...foreground]) {
            if (item.stalled) continue;
            if (t - item.start >= FOREGROUND_STALL_MS) {
                item.stalled = true;
                record({ kind: item.kind, path: item.path, host: item.host, start: item.start, end: t, outcome: 'stalled' });
                item.onStall?.();
                continue;
            }
            count += 1;
            wake = Math.min(wake, item.start + FOREGROUND_STALL_MS);
        }
        return { count, wake };
    }

    /* A foreground picture starting: in-flight background work that costs
       less to stop than to finish gives the link up (rule 3b). */
    function preemptBackground(except: Entry | null, url: string | null) {
        const stop: Entry[] = [];
        for (const entry of active.values()) {
            if (entry === except || entry.state !== 'running' || entry.foreground || entry.pinned) continue;
            if (steadyScopeOf(entry)) continue;
            if (!isBackgroundClass(entry.cls) || entry.transport === 'read') continue;
            if (url && entry.url === url) continue;
            if (entry.cls === 'background-idle') {
                stop.push(entry);
            } else if (entry.transport === 'fetch') {
                const underRule = !entry.answered
                    || entry.received === 0
                    || (entry.total > 0 && entry.received / entry.total < PREEMPT_BELOW);
                if (underRule) stop.push(entry);
            }
            // A soon picture carried by an Image reports no progress: it finishes.
        }
        // Back to the head of their queues, in the order they were running.
        for (let i = stop.length - 1; i >= 0; i -= 1) abortEntry(stop[i], true);
    }

    /* ── Entries ────────────────────────────────────────────────────────── */

    function createEntry(url: string | null, cls: LoadClass, decode: boolean): Entry {
        return {
            key: url || '',
            url,
            cls,
            state: 'queued',
            holders: new Set(),
            primary: null,
            scopeName: null,
            rank: 0,
            decode,
            pinned: false,
            abandoned: false,
            transport: null,
            image: null,
            sentPriority: null,
            bytesIn: false,
            joined: [],
            controller: null,
            attempt: 0,
            received: 0,
            total: 0,
            answered: false,
            startedAt: 0,
            foreground: null,
            decodeTimer: null,
            run: null,
            waiters: [],
        };
    }

    /* A scope's own waiters are answered 'cancelled' when that scope lets go
       of the picture, even while another scope keeps it loading. */
    function waitFor(entry: Entry, scope: Scope | null = null, signal?: AbortSignal): Promise<Settled> {
        return new Promise((resolve) => {
            const waiter = {
                scope,
                resolve: (settled: Settled) => {
                    signal?.removeEventListener('abort', abandon);
                    resolve(settled);
                },
            };
            const abandon = () => {
                const index = entry.waiters.indexOf(waiter);
                if (index === -1) return;
                entry.waiters.splice(index, 1);
                waiter.resolve({ outcome: 'cancelled', image: null });
                entry.abandoned = true;
                cancelAbandonedPicture(entry);
            };
            entry.waiters.push(waiter);
            signal?.addEventListener('abort', abandon, { once: true });
            if (signal?.aborted) abandon();
        });
    }

    function forgetAbandonedPicture(entry: Entry) {
        abandonedPictures.delete(entry);
        if (!abandonedPictures.size) {
            abandonedPictureObserver?.disconnect();
            abandonedPictureObserver = null;
        }
    }

    // Direct card images need no installed scheduler observer. They still own
    // their URL while connected, including a page kept through a handover.
    function hasConnectedPicture(url: string | null) {
        if (!url || typeof document === 'undefined') return false;
        const requested = parseUrl(url)?.href;
        return Boolean(requested) && Array.from(document.images).some((image) => (
            parseUrl(image.getAttribute('src') || image.currentSrc || '')?.href === requested
        ));
    }

    function cancelAbandonedPicture(entry: Entry) {
        if (active.get(entry.key) !== entry || !entry.abandoned) return;
        if (entry.waiters.length || entry.holders.size || entry.pinned) return;
        if (hasConnectedPicture(entry.url)) {
            abandonedPictures.add(entry);
            if (!abandonedPictureObserver && typeof MutationObserver === 'function') {
                abandonedPictureObserver = new MutationObserver(() => {
                    for (const picture of [...abandonedPictures]) cancelAbandonedPicture(picture);
                });
                abandonedPictureObserver.observe(document, {
                    childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'],
                });
            }
            return;
        }
        forgetAbandonedPicture(entry);
        abortEntry(entry, false);
    }

    function addHolder(entry: Entry, scope: Scope) {
        entry.holders.add(scope);
        scope.entries.add(entry);
        if (!entry.primary) entry.primary = scope;
        if (!entry.scopeName) entry.scopeName = scope.name;
    }

    function enqueue(entry: Entry, atHead = false) {
        if (!isBackgroundClass(entry.cls)) return;
        const queue = queues[entry.cls];
        if (atHead) {
            queue.unshift(entry);
            return;
        }
        let index = queue.length;
        while (index > 0 && queue[index - 1].rank > entry.rank) index -= 1;
        queue.splice(index, 0, entry);
    }

    /* A picture still waiting, asked for again at a lower rank (the flow's
       next view naming a picture another surface queued first), moves up to
       that rank; a higher rank never moves it down. */
    function rerank(entry: Entry, rank: number) {
        if (entry.state !== 'queued' || !(rank < entry.rank) || !isBackgroundClass(entry.cls)) return;
        removeFromQueue(entry);
        entry.rank = rank;
        enqueue(entry);
    }

    function removeFromQueue(entry: Entry) {
        for (const queue of Object.values(queues)) {
            const index = queue.indexOf(entry);
            if (index !== -1) queue.splice(index, 1);
        }
    }

    function cancelImage(image: HTMLImageElement | null) {
        if (!image) return;
        image.onload = null;
        image.onerror = null;
        if (typeof image.removeAttribute === 'function') image.removeAttribute('src');
        else image.src = '';
    }

    /* A settled picture's earlier elements are only let go of; a stopped or
       failed one's are stopped too. */
    function releaseJoined(entry: Entry, stop: boolean) {
        const joined = entry.joined.splice(0);
        if (stop) joined.forEach(cancelImage);
    }

    function wantsDecode(entry: Entry) {
        return entry.decode || isForegroundClass(entry.cls);
    }

    function settle(entry: Entry, outcome: Outcome, image: HTMLImageElement | null, decoded = false, value?: unknown) {
        if (active.get(entry.key) !== entry) return;
        forgetAbandonedPicture(entry);
        active.delete(entry.key);
        removeFromQueue(entry);
        entry.attempt += 1;
        if (entry.decodeTimer !== null) clearTimeout(entry.decodeTimer);
        entry.decodeTimer = null;
        if (entry.image) {
            if (outcome === 'ready') {
                entry.image.onload = null;
                entry.image.onerror = null;
            } else {
                cancelImage(entry.image);
            }
        }
        entry.image = null;
        releaseJoined(entry, outcome !== 'ready');
        if (entry.controller && outcome !== 'ready') {
            try { entry.controller.abort(); } catch (_) { /* already settled */ }
        }
        entry.controller = null;
        const item = entry.foreground;
        entry.foreground = null;
        if (item) foreground.delete(item);
        const holders = [...entry.holders];
        entry.holders.clear();
        for (const scope of holders) {
            scope.entries.delete(entry);
            checkDrained(scope);
        }
        if (outcome === 'ready' && entry.url) rememberSettled(entry.url, decoded ? 'decoded' : 'bytes');
        if (entry.startedAt) {
            const described = entry.url ? describe(entry.url) : { path: `read:${entry.scopeName || ''}`, host: '' };
            record({
                kind: entry.run ? 'read' : 'picture',
                ...described,
                cls: entry.cls,
                scope: entry.scopeName,
                mode: entry.url ? requestMode(entry.url).mode : null,
                transport: entry.transport,
                start: entry.startedAt,
                end: now(),
                bytes: entry.transport === 'fetch' ? entry.received : null,
                outcome: outcome === 'ready' ? 'ok' : outcome === 'failed' ? 'error' : 'cancelled',
            });
        }
        const waiters = entry.waiters.splice(0);
        for (const waiter of waiters) {
            try {
                waiter.resolve({ outcome, image: outcome === 'ready' ? image : null, value });
            } catch (_) {
                // One waiter cannot starve the others.
            }
        }
        pump();
    }

    /* Stops background work or an explicitly abandoned sole consumer.
       Requeued work goes back to the head of its queue. */
    function abortEntry(entry: Entry, requeue: boolean) {
        if (active.get(entry.key) !== entry) return;
        if (entry.state !== 'running') {
            if (!requeue) settle(entry, 'cancelled', null);
            return;
        }
        entry.attempt += 1;
        const lost = entry.received;
        if (entry.decodeTimer !== null) clearTimeout(entry.decodeTimer);
        entry.decodeTimer = null;
        cancelImage(entry.image);
        entry.image = null;
        releaseJoined(entry, true);
        if (entry.controller) {
            try { entry.controller.abort(); } catch (_) { /* already settled */ }
        }
        entry.controller = null;
        record({
            kind: entry.run ? 'read' : 'picture',
            ...describe(entry.url),
            cls: entry.cls,
            scope: entry.scopeName,
            mode: entry.url ? requestMode(entry.url).mode : null,
            transport: entry.transport,
            start: entry.startedAt,
            end: now(),
            bytes: entry.transport === 'fetch' ? lost : null,
            outcome: 'aborted',
            lostBytes: lost,
        });
        entry.received = 0;
        entry.total = 0;
        entry.answered = false;
        entry.bytesIn = false;
        entry.sentPriority = null;
        entry.transport = entry.run ? 'read' : null;
        if (!requeue) {
            // Logged once, as the abort above.
            entry.state = 'queued';
            entry.startedAt = 0;
            settle(entry, 'cancelled', null);
            return;
        }
        entry.state = 'queued';
        enqueue(entry, true);
    }

    /* ── Transports ─────────────────────────────────────────────────────── */

    function startImage(entry: Entry, priority: RequestPriority, keepStart = false) {
        const attempt = ++entry.attempt;
        entry.state = 'running';
        entry.transport = 'image';
        entry.sentPriority = priority;
        if (!keepStart || !entry.startedAt) entry.startedAt = now();
        let image: HTMLImageElement;
        try {
            image = new Image();
        } catch (_) {
            settle(entry, 'failed', null);
            return;
        }
        entry.image = image;
        const current = () => entry.attempt === attempt && entry.image === image;
        // Fixed before src starts the request: a picture later sampled into a
        // canvas must never be fetched without CORS (and a same-origin one
        // never with it).
        const { crossOrigin } = requestMode(entry.url);
        if (crossOrigin) image.crossOrigin = crossOrigin;
        if (priority) image.fetchPriority = priority;
        try {
            image.decoding = 'async';
        } catch (_) {
            // Read-only in an old engine.
        }
        image.onload = () => {
            if (!current()) return;
            image.onload = null;
            image.onerror = null;
            entry.bytesIn = true;
            if (!wantsDecode(entry)) {
                settle(entry, 'ready', image);
                return;
            }
            decodeAndRetain(entry, image, attempt);
        };
        image.onerror = () => {
            if (current()) settle(entry, 'failed', null);
        };
        image.src = entry.url;
    }

    function decodeAndRetain(entry: Entry, image: HTMLImageElement, attempt: number) {
        const current = () => entry.attempt === attempt && entry.image === image;
        const finish = () => {
            if (!current()) return;
            retain(entry.url, image);
            settle(entry, 'ready', image, true);
        };
        if (typeof image.decode !== 'function') {
            finish();
            return;
        }
        // The bytes are in the cache; only the decoder can hang now.
        entry.decodeTimer = setTimeout(() => {
            entry.decodeTimer = null;
            if (current()) settle(entry, 'failed', null);
        }, DECODE_CAP_MS);
        let decoding: Promise<void>;
        try {
            decoding = Promise.resolve(image.decode());
        } catch (error) {
            decoding = Promise.reject(error);
        }
        // A successful load with intrinsic pixels is drawable even when
        // decode() rejects (observed in Firefox and for some format
        // combinations): keep that authority rather than none.
        decoding.then(finish, finish);
    }

    function startFetchBytes(entry: Entry) {
        const attempt = ++entry.attempt;
        entry.state = 'running';
        entry.transport = 'fetch';
        entry.sentPriority = 'low';
        entry.bytesIn = false;
        entry.startedAt = now();
        entry.received = 0;
        entry.total = 0;
        entry.answered = false;
        const controller = new AbortController();
        entry.controller = controller;
        backgroundSignals.add(controller.signal);
        const current = () => entry.attempt === attempt && entry.controller === controller;
        const init = { ...requestMode(entry.url).init, priority: 'low', signal: controller.signal } as RequestInit;
        let request: Promise<Response>;
        try {
            request = Promise.resolve(window.fetch(entry.url, init));
        } catch (error) {
            request = Promise.reject(error);
        }
        request.then(async (response) => {
            if (!current()) {
                // Stopped, or joined by a picture asked for again: an unread
                // body would hold the response open.
                try { void response?.body?.cancel?.(); } catch (_) { /* gone */ }
                return;
            }
            if (!response || (!response.ok && response.type !== 'opaque')) throw new Error('background download failed');
            entry.answered = true;
            const length = Number(response.headers?.get?.('content-length'));
            if (Number.isFinite(length) && length > 0) entry.total = length;
            const reader = response.body?.getReader?.();
            if (reader) {
                for (;;) {
                    const { done, value } = await reader.read();
                    if (!current()) {
                        try { void reader.cancel(); } catch (_) { /* gone */ }
                        return;
                    }
                    if (done) break;
                    entry.received += value?.byteLength || 0;
                }
            } else if (response.type !== 'opaque' && typeof response.arrayBuffer === 'function') {
                const buffer = await response.arrayBuffer();
                if (!current()) return;
                entry.received = buffer.byteLength;
            }
            if (!current()) return;
            entry.controller = null;
            entry.bytesIn = true;
            if (!wantsDecode(entry)) {
                settle(entry, 'ready', null);
                return;
            }
            // The bytes are cached; the pixels come from the same URL in the
            // same mode, so this is a cache hit.
            startImage(entry, entry.cls === 'foreground-gated' ? 'high' : null, true);
        }).catch(() => {
            if (current()) settle(entry, 'failed', null);
        });
    }

    function startRead(entry: Entry) {
        const attempt = ++entry.attempt;
        entry.state = 'running';
        entry.transport = 'read';
        entry.startedAt = now();
        const controller = new AbortController();
        entry.controller = controller;
        backgroundSignals.add(controller.signal);
        const current = () => entry.attempt === attempt && entry.controller === controller;
        let running: Promise<unknown>;
        try {
            running = Promise.resolve(entry.run(controller.signal));
        } catch (error) {
            running = Promise.reject(error);
        }
        running.then(
            (value) => {
                if (!current()) return;
                entry.controller = null;
                settle(entry, 'ready', null, false, value);
            },
            () => {
                if (!current()) return;
                const aborted = controller.signal.aborted;
                entry.controller = null;
                settle(entry, aborted ? 'cancelled' : 'failed', null);
            },
        );
    }

    /* WebKit: the flow's next view (a rank below 0, see soon()) goes at the
       priority its page turn will ask for, since nothing raises it then. Its
       caller asks for it only once the screen's own pictures are in (the
       Collection's look-ahead: collection.js armLookAhead). At the browser's
       own priority it waited behind the Collection's films, which WebKit
       sends above a picture's default and which keep asking for byte ranges,
       was still on the wire at the press, and the page turn took as long as
       with soon work sent 'low' (see the header). */
    function backgroundPriority(entry: Entry): RequestPriority {
        if (entry.cls === 'background-soon' && entry.rank < 0 && requestPriorityIsFinal()) return freshPriority('foreground-gated');
        return freshPriority(entry.cls);
    }

    function startBackground(entry: Entry) {
        if (entry.run) startRead(entry);
        else if (useFetchTransport(entry.url)) startFetchBytes(entry);
        else startImage(entry, backgroundPriority(entry));
    }

    function startForegroundPicture(entry: Entry) {
        entry.foreground = addForeground('picture', entry.url, entry);
        // Level with a picture of the same ask that WebKit keeps below it:
        // the browser's own priority, never lower (see the header).
        startImage(entry, asking?.levelled ? null : freshPriority(entry.cls));
    }

    /* A download already sent, now wanted at a higher priority than it was
       sent with. Setting fetchPriority on its element changes nothing once
       the request is out, in any engine: Chromium kept the promoted picture
       at urgency 3 behind the page's fresh urgency-1 pictures, and it came in
       LAST (round 14 review, 27 September 2026, Collection Next on the phone
       seats). So it is asked for again, in the same mode, at the new
       priority: the second request joins the one download (one request in
       Chromium, Firefox and WebKit), and Chromium raises it. The first
       element keeps its request until the entry settles: removing its src
       first makes Firefox cancel it and download it again. Where nothing can
       raise a sent request (WebKit), asking again adds nothing: the ask it
       belongs to sends its fresh pictures level with it instead (noteBelow). */
    function askAgain(entry: Entry) {
        if (entry.state !== 'running' || !entry.url || entry.transport === 'read' || entry.bytesIn) return;
        const priority = freshPriority(entry.cls);
        if (!outranks(priority, entry.sentPriority) || requestPriorityIsFinal()) return;
        const earlier = entry.image;
        if (earlier) {
            earlier.onload = null;
            earlier.onerror = null;
            entry.joined.push(earlier);
        }
        // A fetch() download (Chromium) is joined through the HTTP cache,
        // then lets go of its own body.
        startImage(entry, priority, true);
    }

    /* WebKit: a picture of the ask being made that is still on the wire below
       the ask's priority stays there, whether this ask re-classed it or an
       earlier ask sent it level (nothing raises a sent request): the ask's
       fresh pictures go level with it (inAsk). */
    function noteBelow(entry: Entry, cls: LoadClass) {
        if (!asking || !isForegroundClass(cls) || !requestPriorityIsFinal()) return;
        if (entry.state !== 'running' || entry.transport !== 'image' || entry.bytesIn) return;
        if (outranks(freshPriority(cls), entry.sentPriority)) asking.levelled = true;
    }

    /* Intent: up a class, never down. A running download is re-classed and
       asked for again at the new class's priority (askAgain), never
       restarted: counting it as foreground pauses everything else. */
    function raise(entry: Entry, cls: LoadClass) {
        if (CLASS_RANK[cls] <= CLASS_RANK[entry.cls]) return;
        entry.cls = cls;
        if (isForegroundClass(cls)) {
            entry.decode = true;
            if (entry.state === 'queued') {
                removeFromQueue(entry);
                startForegroundPicture(entry);
                return;
            }
            if (!entry.foreground) entry.foreground = addForeground('picture', entry.url, entry);
            askAgain(entry);
            return;
        }
        if (entry.state === 'queued') {
            removeFromQueue(entry);
            enqueue(entry, true);
            pump();
            return;
        }
        askAgain(entry);
    }

    /* ── Scopes ─────────────────────────────────────────────────────────── */

    function scopeActive(scope: Scope) {
        return !scope.closed && (screen === null || scope.runsOn === '*' || scope.runsOn.includes(screen));
    }

    function checkDrained(scope: Scope) {
        if (scope.entries.size > 0) return;
        const drained = scope.drained.splice(0);
        drained.forEach((resolve) => resolve());
    }

    function releaseHolder(entry: Entry, scope: Scope) {
        entry.holders.delete(scope);
        scope.entries.delete(entry);
        const released = entry.waiters.filter((waiter) => waiter.scope === scope);
        entry.waiters = entry.waiters.filter((waiter) => waiter.scope !== scope);
        released.forEach((waiter) => waiter.resolve({ outcome: 'cancelled', image: null }));
        if (entry.primary === scope) entry.primary = entry.holders.values().next().value || null;
        checkDrained(scope);
        if (entry.holders.size > 0) return;
        if (entry.abandoned) {
            cancelAbandonedPicture(entry);
            return;
        }
        // Foreground work is never cancelled by a scope.
        if (!isBackgroundClass(entry.cls) || entry.foreground) return;
        if (entry.state === 'queued') settle(entry, 'cancelled', null);
        else abortEntry(entry, false);
    }

    function closeScope(scope: Scope) {
        if (scope.closed) return;
        scope.closed = true;
        scopes.delete(scope);
        for (const entry of [...scope.entries]) releaseHolder(entry, scope);
        scope.probes.clear();
        checkDrained(scope);
        pump();
    }

    function tally(outcomes: Outcome[]): GateResult {
        const result = { ready: 0, failed: 0, cancelled: 0 };
        for (const outcome of outcomes) {
            if (outcome === 'ready') result.ready += 1;
            else if (outcome === 'failed') result.failed += 1;
            else result.cancelled += 1;
        }
        return result;
    }

    /* Several pictures asked for at once (a page, a hand, a promote). The
       ones already on the wire are raised first, so the fresh ones are sent
       knowing whether one of them stayed below the ask's priority (WebKit,
       noteBelow): then they go level with it, and WebKit, serving one
       priority in the order sent, brings the one on the wire in first. */
    function inAsk(urls: ReadonlyArray<string | null>, each: (url: string | null, index: number) => void) {
        const onWire = (url: string | null) => Boolean(url) && active.get(url)?.state === 'running';
        const order = urls.map((_, index) => index);
        order.sort((a, b) => Number(onWire(urls[b])) - Number(onWire(urls[a])));
        const outer = asking;
        asking = { levelled: false };
        try {
            for (const index of order) each(urls[index], index);
        } finally {
            asking = outer;
        }
    }

    function acquireAll(urls: ReadonlyArray<string | null>, cls: LoadClass, options: PictureRequestOptions = {}): Array<Promise<Settled>> {
        const settled: Array<Promise<Settled>> = [];
        inAsk(urls, (url, index) => { settled[index] = acquire(url, cls, options); });
        return settled;
    }

    function acquire(url: string | null, cls: LoadClass, { signal }: PictureRequestOptions = {}): Promise<Settled> {
        if (signal?.aborted) return Promise.resolve({ outcome: 'cancelled', image: null });
        ensureListening();
        if (!url) return Promise.resolve({ outcome: 'failed', image: null });
        if (decodedFresh(url)) return Promise.resolve({ outcome: 'ready', image: samplerImage(url) });
        const owner = isBackgroundClass(cls)
            ? (signal ? makeScope('image-warm', { runsOn: '*' }) : internal())
            : null;
        const receive = (entry: Entry) => {
            if (owner) addHolder(entry, owner);
            const settled = waitFor(entry, signal ? owner : null, signal);
            return signal && owner
                ? settled.then((result) => { closeScope(owner); return result; })
                : settled;
        };
        const existing = active.get(url);
        if (existing) {
            existing.decode = true;
            raise(existing, cls);
            noteBelow(existing, cls);
            return receive(existing);
        }
        const entry = createEntry(url, cls, true);
        active.set(url, entry);
        const settled = receive(entry);
        if (active.get(entry.key) !== entry) return settled;
        if (isForegroundClass(cls)) {
            startForegroundPicture(entry);
        } else {
            enqueue(entry);
            pump();
        }
        return settled;
    }

    function declare(scope: Scope, pictures: ReadonlyArray<PictureLike>, cls: LoadClass, options?: { decode?: boolean }) {
        ensureListening();
        const list = Array.isArray(pictures) ? pictures : [];
        if (isForegroundClass(cls)) {
            const urls = list.map(pictureUrl).filter(Boolean);
            const asked = scope.closed
                ? urls.map(() => Promise.resolve<Outcome>('cancelled'))
                : acquireAll(urls, cls).map((settled) => settled.then((result) => result.outcome));
            pump();
            return Promise.all(asked).then(tally);
        }
        const outcomes: Array<Promise<Outcome>> = [];
        for (const picture of list) {
            const url = pictureUrl(picture);
            if (!url) continue;
            if (scope.closed) {
                outcomes.push(Promise.resolve('cancelled'));
                continue;
            }
            // Nothing to download: a data: picture decodes on its own.
            if (!isNetworkUrl(url)) {
                outcomes.push(Promise.resolve('ready'));
                continue;
            }
            const decode = options?.decode === true;
            if (decode ? decodedFresh(url) : settledUrls.has(url)) {
                rememberSettled(url, settledUrls.get(url) || 'bytes');
                outcomes.push(Promise.resolve('ready'));
                continue;
            }
            let entry = active.get(url);
            if (entry) {
                addHolder(entry, scope);
                if (decode) entry.decode = true;
                raise(entry, cls);
                rerank(entry, pictureRank(picture));
            } else {
                entry = createEntry(url, cls, decode);
                entry.rank = pictureRank(picture);
                addHolder(entry, scope);
                active.set(url, entry);
                enqueue(entry);
            }
            outcomes.push(waitFor(entry, scope).then((settled) => settled.outcome));
        }
        pump();
        return Promise.all(outcomes).then(tally);
    }

    function declareRead<T>(scope: Scope, cls: unknown, run: (signal: AbortSignal) => Promise<T> | T): Promise<T | undefined> {
        ensureListening();
        if (typeof run !== 'function' || scope.closed) return Promise.resolve(undefined);
        const entry = createEntry(null, toClass(cls, 'background-soon') === 'background-idle' ? 'background-idle' : 'background-soon', false);
        entry.key = `read:${++readSeq}`;
        entry.run = run;
        addHolder(entry, scope);
        active.set(entry.key, entry);
        enqueue(entry);
        const answer = waitFor(entry, scope).then((settled) => (settled.outcome === 'ready' ? settled.value as T : undefined));
        pump();
        return answer;
    }

    function promote(scope: Scope, pictures: ReadonlyArray<PictureLike>, cls: LoadClass) {
        const target = toClass(cls, 'background-soon');
        const urls = (Array.isArray(pictures) ? pictures : []).map(pictureUrl).filter(Boolean);
        inAsk(urls, (url) => {
            if (!url) return;
            const entry = active.get(url);
            if (entry) {
                if (!scope.closed && isBackgroundClass(entry.cls)) addHolder(entry, scope);
                raise(entry, target);
                noteBelow(entry, target);
            } else if (isForegroundClass(target)) {
                void acquire(url, target);
            } else if (!scope.closed && !settledUrls.has(url)) {
                void declare(scope, [url], target);
            }
        });
        pump();
    }

    function makeScope(name: string, options: ScopeOptions = {}): Scope {
        const runsOn = options.runsOn === '*' || !Array.isArray(options.runsOn)
            ? '*'
            : [...options.runsOn];
        const maxInFlight = Number.isFinite(options.maxInFlight) && options.maxInFlight > 0
            ? Math.floor(options.maxInFlight)
            : Infinity;
        const scope: Scope = {
            name: String(name || 'scope'),
            runsOn,
            maxInFlight,
            admit: typeof options.admit === 'function' ? options.admit : null,
            steady: options.steady === true,
            closed: false,
            entries: new Set(),
            probes: new Map(),
            drained: [],
            api: null,
        };
        scope.api = {
            get name() { return scope.name; },
            get closed() { return scope.closed; },
            gated: (pictures) => declare(scope, pictures, 'foreground-gated'),
            show: (pictures) => {
                void acquireAll((Array.isArray(pictures) ? pictures : []).map(pictureUrl), 'foreground');
            },
            soon: (pictures, opts) => declare(scope, pictures, 'background-soon', opts),
            idle: (pictures, opts) => declare(scope, pictures, 'background-idle', opts),
            read: (cls, run) => declareRead(scope, cls, run),
            promote: (pictures, cls) => promote(scope, pictures, cls),
            waitWhile: (reason, isBusy, capMs) => addProbe(scope.probes, reason, isBusy, capMs),
            close: () => closeScope(scope),
            settled: () => (scope.entries.size === 0
                ? Promise.resolve()
                : new Promise<void>((resolve) => { scope.drained.push(resolve); })),
        };
        scopes.add(scope);
        return scope;
    }

    function internal() {
        if (!internalScope || internalScope.closed) internalScope = makeScope('image', { runsOn: '*' });
        return internalScope;
    }

    /* ── Holds and probes ───────────────────────────────────────────────── */

    function addProbe(probes: Map<string, Probe>, reason: string, isBusy: () => boolean, capMs?: number) {
        ensureListening();
        if (typeof isBusy !== 'function') return () => undefined;
        const key = String(reason || 'probe');
        const probe: Probe = {
            isBusy,
            // Infinity: for as long as it is busy (a cover the player chose, not one that can hang).
            capMs: typeof capMs === 'number' && capMs > 0 ? capMs : DEFAULT_WAIT_CAP_MS,
            busySince: null,
            capped: false,
        };
        probes.set(key, probe);
        pump();
        return () => {
            if (probes.get(key) !== probe) return;
            probes.delete(key);
            pump();
        };
    }

    function evaluateProbes(probes: Map<string, Probe>, t: number) {
        let blocked = false;
        let wake = Infinity;
        for (const [reason, probe] of probes) {
            let busy = false;
            try {
                busy = Boolean(probe.isBusy());
            } catch (_) {
                busy = false;
            }
            if (!busy) {
                probe.busySince = null;
                probe.capped = false;
                continue;
            }
            if (probe.busySince === null) probe.busySince = t;
            if (t - probe.busySince >= probe.capMs) {
                if (!probe.capped) {
                    probe.capped = true;
                    record({ kind: 'hold', path: reason, start: probe.busySince, end: t, outcome: 'capped' });
                }
                continue;
            }
            blocked = true;
            wake = Math.min(wake, t + PROBE_POLL_MS, probe.busySince + probe.capMs);
        }
        return { blocked, wake };
    }

    function holdsBlocking(t: number) {
        let blocked = false;
        let wake = Infinity;
        for (const hold of [...holds]) {
            if (hold.until <= t) {
                holds.delete(hold);
                record({ kind: 'hold', path: hold.reason, end: t, outcome: 'capped' });
                continue;
            }
            blocked = true;
            wake = Math.min(wake, hold.until);
        }
        const probes = evaluateProbes(globalProbes, t);
        return { blocked: blocked || probes.blocked, wake: Math.min(wake, probes.wake) };
    }

    /* ── Elements the page loads itself ─────────────────────────────────── */

    function checkMode(img: HTMLImageElement, url: string) {
        if (!isNetworkUrl(url)) return;
        const wantsCors = isCrossOrigin(url);
        const usesCors = img.crossOrigin != null;
        if (wantsCors === usesCors) return;
        const described = describe(url);
        record({ kind: 'mode-mismatch', ...described, mode: usesCors ? 'cors' : 'no-cors', outcome: 'mismatch' });
        const key = `${described.host}${described.path}`;
        if (mismatchWarned.has(key) || mismatchWarned.size >= LEDGER_CAP) return;
        mismatchWarned.add(key);
        console.warn(
            `[LoadScheduler] one picture, two request modes: ${key} is loaded ${usesCors ? 'with' : 'without'} CORS, `
            + `which downloads it a second time beside the ${wantsCors ? 'CORS' : 'no-CORS'} copy (requestMode).`,
        );
    }

    function inspectElement(img: HTMLImageElement, element: Element) {
        if (elements.get(img) !== element) return;
        let src = element.url;
        if (!src) {
            try {
                src = img.getAttribute('src') || null;
            } catch (_) {
                src = null;
            }
        }
        if (!src) {
            element.end();
            return;
        }
        element.sawSrc = true;
        element.url = src;
        Object.assign(element.item, describe(src));
        checkMode(img, src);
        // The same picture already downloading in the background is now what
        // the player waits for: it must never be stopped half-way.
        const entry = active.get(src);
        if (entry && entry.state === 'running') entry.pinned = true;
    }

    function sweepElements() {
        for (const [img, element] of [...elements]) {
            if (!element.sawSrc) continue;
            let src: string | null = null;
            try {
                src = img.getAttribute('src');
            } catch (_) {
                src = null;
            }
            if (!src || img.complete === true) element.end();
        }
    }

    function observeImage(img: HTMLImageElement, cls: LoadClass = 'foreground', url?: string) {
        ensureListening();
        if (!img || typeof img.addEventListener !== 'function' || elements.has(img)) return;
        void toClass(cls, 'foreground');
        const known = typeof url === 'string' && url ? url : null;
        const item = addForeground('element', known, null);
        const element: Element = { item, sawSrc: false, url: known, end: () => undefined };
        const end = () => {
            img.removeEventListener('load', end);
            img.removeEventListener('error', end);
            if (elements.get(img) !== element) return;
            elements.delete(img);
            endForeground(item);
        };
        element.end = end;
        elements.set(img, element);
        img.addEventListener('load', end);
        img.addEventListener('error', end);
        // Called just before the owner sets src: look once it has.
        void Promise.resolve().then(() => inspectElement(img, element));
    }

    /* ── The pump ───────────────────────────────────────────────────────── */

    function clearWake() {
        if (wakeTimer !== null) clearTimeout(wakeTimer);
        wakeTimer = null;
        wakeAt = Infinity;
    }

    function scheduleWake(at: number) {
        if (!Number.isFinite(at)) return;
        if (wakeTimer !== null && wakeAt <= at) return;
        if (wakeTimer !== null) clearTimeout(wakeTimer);
        wakeAt = at;
        wakeTimer = setTimeout(() => {
            wakeTimer = null;
            wakeAt = Infinity;
            pump();
        }, Math.max(0, at - now()));
    }

    function hasQueued() {
        return queues['background-soon'].length > 0 || queues['background-idle'].length > 0;
    }

    function isRunningBackground(entry: Entry) {
        return entry.state === 'running' && isBackgroundClass(entry.cls) && !entry.foreground;
    }

    function countRunning(predicate: (entry: Entry) => boolean) {
        let count = 0;
        for (const entry of active.values()) if (isRunningBackground(entry) && predicate(entry)) count += 1;
        return count;
    }

    function entryActive(entry: Entry) {
        for (const scope of entry.holders) if (scopeActive(scope)) return true;
        return false;
    }

    /* The steady scope an entry runs under: the first of its holders that is
       steady and on its screen. A URL a steady scope and another scope both
       asked for runs at the steady pace. */
    function steadyScopeOf(entry: Entry): Scope | null {
        if (entry.cls !== 'background-soon') return null;
        for (const scope of entry.holders) if (scope.steady && scopeActive(scope)) return scope;
        return null;
    }

    function sameOriginFull(entry: Entry, policy: LoadPolicy) {
        return policy.appOriginHttp1 && Boolean(entry.url) && !isCrossOrigin(entry.url)
            && countRunning((other) => Boolean(other.url) && !isCrossOrigin(other.url)) >= policy.sameOriginSoonLimit;
    }

    /* STEADY: THE MATCH'S ART PLAN RUNS AT ITS OWN PACE. The owner, 28
       September 2026, after draws and enemy summons landed without their art:
       "in game, asap, all heavy assets should be loaded. but loading
       shouldn't wait for that". A steady scope's soon pictures start while no
       picture the screen is loading is on the wire (the caller checks), up to
       the scope's own maxInFlight, in its declared order; the waits after a
       press and after a data read, the idle slot and the global limit of two
       are not theirs, and no foreground picture pre-empts them. Its admit
       rule, probes, screens and close() still apply. */
    function startSteady(policy: LoadPolicy, blockedScopes: Set<Scope>) {
        for (const entry of [...queues['background-soon']]) {
            if (entry.state !== 'queued') continue;
            const scope = steadyScopeOf(entry);
            if (!scope || blockedScopes.has(scope)) continue;
            if (countRunning((other) => steadyScopeOf(other) === scope) >= scope.maxInFlight) continue;
            if (sameOriginFull(entry, policy)) continue;
            removeFromQueue(entry);
            startBackground(entry);
        }
    }

    function canStart(entry: Entry, policy: LoadPolicy, blockedScopes: Set<Scope>) {
        if (steadyScopeOf(entry)) return false;
        let holderReady = false;
        for (const scope of entry.holders) {
            if (scopeActive(scope) && !blockedScopes.has(scope)) {
                holderReady = true;
                break;
            }
        }
        if (!holderReady) return false;
        const primary = entry.primary;
        if (primary && Number.isFinite(primary.maxInFlight)
            && countRunning((other) => other.primary === primary) >= primary.maxInFlight) return false;
        if (sameOriginFull(entry, policy)) return false;
        return true;
    }

    function dropUnadmitted(policy: LoadPolicy) {
        for (const scope of [...scopes]) {
            if (scope.closed || !scope.admit) continue;
            let admitted = true;
            try {
                admitted = scope.admit(policy) !== false;
            } catch (_) {
                admitted = true;
            }
            if (admitted) continue;
            for (const entry of [...scope.entries]) {
                if (entry.state === 'queued') releaseHolder(entry, scope);
            }
        }
    }

    function takeSlot(cls: BackgroundClass) {
        if (slots[cls] === 'granted') {
            slots[cls] = 'none';
            return true;
        }
        if (slots[cls] === 'pending') return false;
        const requestIdle = typeof window !== 'undefined' ? window.requestIdleCallback : undefined;
        if (typeof requestIdle !== 'function') {
            if (cls === 'background-soon') return true;
            slots[cls] = 'pending';
            setTimeout(() => {
                slots[cls] = 'granted';
                pump();
            }, IDLE_FALLBACK_MS);
            return false;
        }
        slots[cls] = 'pending';
        const grant = () => {
            slots[cls] = 'granted';
            pump();
        };
        try {
            if (cls === 'background-soon') requestIdle.call(window, grant, { timeout: SOON_IDLE_SLOT_MS });
            else requestIdle.call(window, grant);
        } catch (_) {
            slots[cls] = 'granted';
        }
        if (slots[cls] !== 'granted') return false;
        slots[cls] = 'none';
        return true;
    }

    function startFrom(cls: BackgroundClass, limit: number, ready: (entry: Entry) => boolean, counted: (entry: Entry) => boolean) {
        for (const entry of [...queues[cls]]) {
            if (countRunning(counted) >= limit) break;
            if (entry.state !== 'queued' || !ready(entry)) continue;
            removeFromQueue(entry);
            startBackground(entry);
        }
    }

    function step() {
        sweepElements();
        if (!hasQueued()) {
            clearWake();
            return;
        }
        const t = now();
        const policy = computePolicy();
        dropUnadmitted(policy);
        if (!hasQueued()) {
            clearWake();
            return;
        }
        const held = holdsBlocking(t);
        if (held.blocked) {
            scheduleWake(held.wake);
            return;
        }
        let wake = Infinity;
        const blockedScopes = new Set<Scope>();
        for (const scope of scopes) {
            if (!scope.probes.size) continue;
            const probes = evaluateProbes(scope.probes, t);
            if (!probes.blocked) continue;
            blockedScopes.add(scope);
            wake = Math.min(wake, probes.wake);
        }
        const busy = foregroundBlocking(t);
        if (busy.count > 0) {
            scheduleWake(Math.min(busy.wake, wake));
            return;
        }
        startSteady(policy, blockedScopes);
        if (t < lastFetchEndAt + FETCH_QUIET_MS) {
            scheduleWake(Math.min(lastFetchEndAt + FETCH_QUIET_MS, wake));
            return;
        }
        const ready = (entry: Entry) => canStart(entry, policy, blockedScopes);
        const unsteady = (entry: Entry) => !steadyScopeOf(entry);

        const soonWaiting = queues['background-soon'].some((entry) => unsteady(entry) && entryActive(entry));
        if (soonWaiting) {
            if (t < lastInputAt + SOON_INPUT_QUIET_MS) {
                scheduleWake(lastInputAt + SOON_INPUT_QUIET_MS);
                return;
            }
            // An idle download already running counts against soon's limit:
            // background stays at two in flight in all (a steady scope's own
            // downloads are counted by that scope alone).
            if (countRunning(unsteady) < policy.soonLimit
                && queues['background-soon'].some(ready)
                && takeSlot('background-soon')) {
                startFrom('background-soon', policy.soonLimit, ready, unsteady);
            }
            scheduleWake(wake);
            return;
        }
        if (countRunning((entry) => entry.cls === 'background-soon') > 0) {
            scheduleWake(wake);
            return;
        }
        if (!policy.idleAllowed || pageHidden()) return;
        if (!queues['background-idle'].some(ready)) {
            scheduleWake(wake);
            return;
        }
        if (countRunning((entry) => entry.cls === 'background-idle') >= IDLE_LIMIT) return;
        if (t < lastInputAt + IDLE_INPUT_QUIET_MS) {
            scheduleWake(lastInputAt + IDLE_INPUT_QUIET_MS);
            return;
        }
        if (takeSlot('background-idle')) {
            startFrom('background-idle', IDLE_LIMIT, ready, (entry) => entry.cls === 'background-idle');
        }
    }

    function pump() {
        if (pumping) {
            repump = true;
            return;
        }
        pumping = true;
        try {
            let rounds = 0;
            do {
                repump = false;
                step();
                rounds += 1;
            } while (repump && rounds < 32);
        } finally {
            pumping = false;
            // An idle slot is a moment, not a voucher: one that arrived while
            // background was held is asked for again when it may start.
            for (const cls of Object.keys(slots) as BackgroundClass[]) {
                if (slots[cls] === 'granted') slots[cls] = 'none';
            }
        }
    }

    /* ── Listeners, and the fetch observer ──────────────────────────────── */

    function onConnectionChange() {
        if (!computePolicy().idleAllowed) {
            for (const entry of [...active.values()]) {
                if (isRunningBackground(entry) && entry.cls === 'background-idle' && entry.transport !== 'read') abortEntry(entry, true);
            }
        }
        pump();
    }

    function ensureListening(win: Window = typeof window !== 'undefined' ? window : undefined) {
        if (listening || !win) return;
        listening = true;
        const onInput = () => {
            lastInputAt = now();
            if (hasQueued()) scheduleWake(lastInputAt + SOON_INPUT_QUIET_MS);
        };
        for (const type of INPUT_EVENTS) {
            try {
                win.addEventListener(type, onInput, { capture: true, passive: true });
            } catch (_) {
                // A window without that event still schedules.
            }
        }
        try {
            win.document?.addEventListener('visibilitychange', () => pump());
        } catch (_) {
            // No document: nothing is hidden.
        }
        try {
            (navigator as any).connection?.addEventListener?.('change', onConnectionChange);
        } catch (_) {
            // No Network Information API.
        }
    }

    function requestUrl(input: unknown) {
        if (typeof input === 'string') return input;
        if (input && typeof input === 'object' && typeof (input as Request).url === 'string') return (input as Request).url;
        try {
            return String(input);
        } catch (_) {
            return '';
        }
    }

    function finishFetch(pending: PendingFetch, url: string) {
        if (pending.done) return;
        pending.done = true;
        const list = pendingFetches.get(url);
        if (list) {
            const index = list.indexOf(pending);
            if (index !== -1) list.splice(index, 1);
            if (!list.length) pendingFetches.delete(url);
        }
        endForeground(pending.item);
    }

    function isAnalytics(url: URL) {
        return ANALYTICS_HOST.test(url.hostname)
            || (url.origin === pageOrigin() && ANALYTICS_PROXY_PATH.test(url.pathname));
    }

    /* A read that does not count has no stall cap to end it; one whose body
       never reports (a keepalive outliving its page) goes at the same age. */
    function pruneUncounted(t: number) {
        for (const [url, list] of [...pendingFetches]) {
            for (const pending of [...list]) {
                if (!pending.item && t - pending.start >= FOREGROUND_STALL_MS) finishFetch(pending, url);
            }
        }
    }

    function noteFetch(input: unknown, init: RequestInit | undefined) {
        const request = input && typeof input === 'object' ? input as Request : null;
        const url = parseUrl(requestUrl(input));
        // A blob: or data: read never touches the network (and never gets a
        // Resource Timing entry to end it).
        if (!url || (url.protocol !== 'http:' && url.protocol !== 'https:')) return null;
        const signal = init?.signal || request?.signal || null;
        const counts = !(signal && backgroundSignals.has(signal))
            && !(init?.keepalive || request?.keepalive)
            && !isAnalytics(url);
        const start = perfNow();
        pruneUncounted(start);
        const item = counts ? addForeground('fetch', url.href, null) : null;
        const pending: PendingFetch = { item, start, done: false };
        const list = pendingFetches.get(url.href) || [];
        list.push(pending);
        pendingFetches.set(url.href, list);
        if (item) item.onStall = () => finishFetch(pending, url.href);
        return { pending, url: url.href };
    }

    function watchFetch(noted: { pending: PendingFetch; url: string }, result: unknown) {
        const promise = result as Promise<Response>;
        if (!promise || typeof promise.then !== 'function') {
            finishFetch(noted.pending, noted.url);
            return;
        }
        promise.then(
            (response) => {
                try {
                    if (response && response.type === 'cors' && response.ok && corsFetchOrigins.size < LEARNED_ORIGIN_CAP) {
                        corsFetchOrigins.add(new URL(noted.url).origin);
                    }
                } catch (_) {
                    // Nothing learned.
                }
                // Headers are in; the body still arrives. Resource Timing
                // reports when it has, or the stall cap does.
                if (!resourceTimingObserved) finishFetch(noted.pending, noted.url);
            },
            () => finishFetch(noted.pending, noted.url),
        );
    }

    function wrapFetch(win: Window) {
        const current = win.fetch as typeof fetch & { [ORIGINAL_FETCH]?: typeof fetch };
        if (typeof current !== 'function') return;
        const original = current[ORIGINAL_FETCH] || current;
        let noting = false;
        const observed = function (this: unknown, input: RequestInfo | URL, init?: RequestInit) {
            let noted = null;
            if (!noting) {
                noting = true;
                try {
                    noted = noteFetch(input, init);
                } catch (_) {
                    noted = null;
                } finally {
                    noting = false;
                }
            }
            let result: Promise<Response>;
            try {
                // eslint-disable-next-line prefer-rest-params
                result = original.apply(this, arguments as any);
            } catch (error) {
                if (noted) finishFetch(noted.pending, noted.url);
                throw error;
            }
            if (noted) {
                try {
                    watchFetch(noted, result);
                } catch (_) {
                    finishFetch(noted.pending, noted.url);
                }
            }
            return result;
        } as typeof fetch & { [ORIGINAL_FETCH]?: typeof fetch };
        observed[ORIGINAL_FETCH] = original;
        win.fetch = observed;
    }

    function observeResourceTiming(win: Window) {
        const Observer = (win as any).PerformanceObserver;
        if (typeof Observer !== 'function') return;
        try {
            const observer = new Observer((list: PerformanceObserverEntryList) => {
                for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
                    if (entry.initiatorType !== 'fetch') continue;
                    const pendings = pendingFetches.get(entry.name);
                    if (!pendings?.length) continue;
                    // The NEWEST read of that URL still open that started no
                    // later than this body (1 ms for clock rounding). Reads of
                    // one URL finish out of order (every Convex query posts to
                    // /api/query): taking the oldest would end a slow read
                    // still running and leave no read the fast one's body
                    // could claim, holding background until the stall cap.
                    let match: PendingFetch | null = null;
                    for (let i = pendings.length - 1; i >= 0 && !match; i -= 1) {
                        if (pendings[i].start <= entry.startTime + 1) match = pendings[i];
                    }
                    if (match) finishFetch(match, entry.name);
                }
            });
            observer.observe({ type: 'resource', buffered: false });
            resourceTimingObserved = true;
        } catch (_) {
            resourceTimingObserved = false;
        }
    }

    function install(win: Window = typeof window !== 'undefined' ? window : undefined) {
        if (!win) return;
        ensureListening(win);
        if (installed) return;
        installed = true;
        wrapFetch(win);
        observeResourceTiming(win);
        try {
            // The sampler's own loader becomes the shared, mode-true, never
            // timer-cancelled Image (when the sampler offers the hook).
            (win as any).MoonfallRasterSampler?.setImageLoader?.((url: string) => api.image(url, 'foreground'));
        } catch (_) {
            // The sampler keeps its own loader.
        }
    }

    /* ── Public API ─────────────────────────────────────────────────────── */

    const api = {
        install,
        scope(name: string, options?: ScopeOptions): LoadScope {
            ensureListening();
            return makeScope(name, options || {}).api;
        },
        enter(next: ScreenName) {
            ensureListening();
            screen = next || null;
            for (const scope of [...scopes]) {
                if (next === 'match-loading' && scope.runsOn !== '*' && !scope.runsOn.includes('match-loading')) closeScope(scope);
            }
            // A paused scope's idle downloads stop; its soon ones finish.
            for (const entry of [...active.values()]) {
                if (isRunningBackground(entry) && entry.cls === 'background-idle'
                    && entry.transport !== 'read' && !entryActive(entry)) abortEntry(entry, true);
            }
            pump();
        },
        hold(reason: string, capMs?: number) {
            ensureListening();
            const cap = Number.isFinite(capMs) && capMs > 0 ? capMs : DEFAULT_HOLD_CAP_MS;
            const hold = { reason: String(reason || 'hold'), until: now() + cap };
            holds.add(hold);
            let released = false;
            return () => {
                if (released) return;
                released = true;
                if (holds.delete(hold)) pump();
            };
        },
        holdWhile(reason: string, isBusy: () => boolean, capMs?: number) {
            return addProbe(globalProbes, reason, isBusy, capMs);
        },
        observeImage,
        image(url: string, cls: LoadClass = 'foreground', options: PictureRequestOptions = {}): Promise<HTMLImageElement | null> {
            return acquire(url || null, toClass(cls, 'foreground'), options).then((settled) => settled.image);
        },
        /* Several asked for at once, one promise each (inAsk). */
        images(urls: ReadonlyArray<string>, cls: LoadClass = 'foreground', options: PictureRequestOptions = {}): Array<Promise<HTMLImageElement | null>> {
            const list = Array.isArray(urls) ? urls.map((url) => (typeof url === 'string' && url) || null) : [];
            return acquireAll(list, toClass(cls, 'foreground'), options).map((settled) => settled.then((result) => result.image));
        },
        requestMode(url: string): RequestMode {
            return requestMode(url);
        },
        policy(): LoadPolicy {
            return computePolicy();
        },
        ledger(): ReadonlyArray<LedgerEntry> {
            return ledgerEntries.map((entry) => ({ ...entry }));
        },
        /* What is holding or running right now (diagnostics and the meter). */
        state() {
            const t = now();
            const held = [...holds].filter((hold) => hold.until > t).map((hold) => hold.reason);
            for (const [reason, probe] of globalProbes) {
                try {
                    if (probe.isBusy()) held.push(reason);
                } catch (_) {
                    // A failing probe holds nothing.
                }
            }
            let foregroundCount = 0;
            for (const item of foreground) if (!item.stalled) foregroundCount += 1;
            return {
                screen,
                foreground: foregroundCount,
                held,
                queued: {
                    soon: queues['background-soon'].length,
                    idle: queues['background-idle'].length,
                },
                running: {
                    soon: countRunning((entry) => entry.cls === 'background-soon'),
                    idle: countRunning((entry) => entry.cls === 'background-idle'),
                },
            };
        },
    };

    return api;
})();

export { LoadScheduler };
